// ============================================================
// O PLANO DE AÇÃO DA ALOCAÇÃO (Story 34.4, Epic 34; migration 0061)
//
// O plano é ferramenta de TRABALHO, editável pelos dois lados (decisão 8 do #585):
// o cliente pelo portal, a Genesis pelo app interno. Este ficheiro é o contrato
// entre as telas e as duas APIs (`/api/portal/allocations/:id/actions` e
// `/api/allocations/:id/actions`) e a regra do cumprimento de objetivos, que a API
// não calcula e a tela mostra.
//
// ⚠️ O QUE O CORPO NÃO TRAZ: o lado de quem cria (`createdSide`), a pessoa e a data
// da conclusão. O lado deriva da ROTA, a pessoa da LINHA da alocação, e a data da
// conclusão do servidor, no dia em que o status passa a `done`. Os campos não são
// declarados e o Zod descarta-os — o molde de `CreateAllocationNoteSchema`.
// ============================================================
import { z } from 'zod'

import { ContractDateSchema } from './dtos.ts'

/** O responsável é um PAPEL da alocação, e não uma conta: as três pessoas dela. */
export const ALLOCATION_ACTION_OWNER_ROLES = [
  'professional',
  'customer_manager',
  'account_manager',
] as const
export type AllocationActionOwnerRole = (typeof ALLOCATION_ACTION_OWNER_ROLES)[number]

export const ALLOCATION_ACTION_STATUSES = ['planned', 'in_progress', 'pending', 'done'] as const
export type AllocationActionStatus = (typeof ALLOCATION_ACTION_STATUSES)[number]

export const ALLOCATION_ACTION_TITLE_MAX = 200

const ActionFields = {
  title: z.string().trim().min(1).max(ALLOCATION_ACTION_TITLE_MAX),
  ownerRole: z.enum(ALLOCATION_ACTION_OWNER_ROLES),
  /** Sem prazo, a ação não entra no cumprimento. `null` limpa-o. */
  dueOn: ContractDateSchema.nullable(),
  status: z.enum(ALLOCATION_ACTION_STATUSES),
}

export const CreateAllocationActionSchema = z.object({
  title: ActionFields.title,
  ownerRole: ActionFields.ownerRole,
  dueOn: ActionFields.dueOn.optional(),
  status: ActionFields.status.optional(),
})
export type CreateAllocationAction = z.infer<typeof CreateAllocationActionSchema>

export const UpdateAllocationActionSchema = z
  .object(ActionFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo.')
export type UpdateAllocationAction = z.infer<typeof UpdateAllocationActionSchema>

/** A ação como o portal a recebe. A Genesis recebe a linha inteira. */
export interface AllocationActionView {
  id: string
  title: string
  ownerRole: AllocationActionOwnerRole
  dueOn: string | null
  status: AllocationActionStatus
  /** O dia em São Paulo em que passou a `done`; `null` enquanto não está concluída. */
  completedOn: string | null
  createdSide: 'customer' | 'genesis'
}

/** O que o cumprimento lê de uma ação. `completedOn` existe sse o status é `done`. */
export type ComplianceAction = Pick<AllocationActionView, 'dueOn' | 'completedOn'>

export interface ObjectiveCompliance {
  /** As ações com prazo no período e com desfecho já conhecido. */
  due: number
  /** Dessas, as concluídas até o prazo (inclusive). */
  onTime: number
  /** `onTime / due`, ou `null` sem nenhuma ação a contar — a tela mostra «—», não 0%. */
  ratio: number | null
}

/**
 * O CUMPRIMENTO DE OBJETIVOS (decisão 9 do #585): das ações com prazo no período,
 * a fração concluída até o prazo.
 *
 * Só conta a ação cujo desfecho já se sabe: concluída, ou com o prazo vencido
 * (`dueOn < hoje`). No período corrente, uma ação aberta que vence hoje ou depois
 * ainda pode ser cumprida, e contá-la como falha baixaria o indicador por um
 * prazo que não passou. Uma ação concluída DEPOIS do prazo conta, e não cumpriu.
 *
 * As datas são `AAAA-MM-DD` e comparam-se como texto (o cabeçalho da 0034). `hoje`
 * vem de quem chama, como em `isLiveOn`.
 */
export function objectiveCompliance(
  actions: readonly ComplianceAction[],
  period: { from: string; to: string },
  hoje: string,
): ObjectiveCompliance {
  let due = 0
  let onTime = 0
  for (const { dueOn, completedOn } of actions) {
    if (dueOn === null || dueOn < period.from || dueOn > period.to) continue
    if (completedOn === null && dueOn >= hoje) continue
    due += 1
    if (completedOn !== null && completedOn <= dueOn) onTime += 1
  }
  return { due, onTime, ratio: due === 0 ? null : onTime / due }
}
